// Minimal interfaces for what our functions actually need
interface ColumnLike {
  getSize(): number;
  columnDef: {
    minSize?: number;
    maxSize?: number;
  };
}

interface HeaderLike {
  column: ColumnLike;
}

interface CellLike {
  column: ColumnLike;
}

/**
 * Gets the constrained size for a TanStack Table column, ensuring it respects
 * minSize and maxSize constraints even if getSize() doesn't properly enforce them.
 *
 * @param column - The TanStack Table column object
 * @returns The size value constrained within min/max bounds
 */
export function getConstrainedColumnSize(column: ColumnLike): number {
  const size = column.getSize();
  const minSize = column.columnDef.minSize;
  const maxSize = column.columnDef.maxSize;

  if (minSize && size < minSize) return minSize;
  if (maxSize && size > maxSize) return maxSize;
  return size;
}

/**
 * Gets the constrained size from a header object
 *
 * @param header - The TanStack Table header object
 * @returns The size value constrained within min/max bounds
 */
export function getConstrainedHeaderSize(header: HeaderLike): number {
  return getConstrainedColumnSize(header.column);
}

/**
 * Gets the constrained size from a cell object
 *
 * @param cell - The TanStack Table cell object
 * @returns The size value constrained within min/max bounds
 */
export function getConstrainedCellSize(cell: CellLike): number {
  return getConstrainedColumnSize(cell.column);
}

/**
 * Creates a style object with the constrained width for a column
 *
 * @param column - The TanStack Table column object
 * @returns Style object with width property
 */
export function getColumnWidthStyle(column: ColumnLike): React.CSSProperties {
  return {
    width: getConstrainedColumnSize(column),
  };
}

/**
 * Creates a style object with the constrained width for a header
 *
 * @param header - The TanStack Table header object
 * @returns Style object with width property
 */
export function getHeaderWidthStyle(header: HeaderLike): React.CSSProperties {
  return {
    width: getConstrainedHeaderSize(header),
  };
}

/**
 * Creates a style object with the constrained width for a cell
 *
 * @param cell - The TanStack Table cell object
 * @returns Style object with width property
 */
export function getCellWidthStyle(cell: CellLike): React.CSSProperties {
  return {
    width: getConstrainedCellSize(cell),
  };
}
